import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { gsap } from 'gsap';

const EasterEggContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
`;

const SecretMessage = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.9), rgba(68, 255, 153, 0.8));
  color: #000;
  padding: 2rem;
  border-radius: 15px;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 255, 136, 0.3);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(0, 255, 136, 0.5);
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all 0.5s ease;
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
  max-width: 400px;
  
  .close-btn {
    position: absolute;
    top: 10px;
    right: 15px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #000;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
  }
`;

const Achievement = styled.div<{ show: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #00ff88, #44ff99);
  color: #000;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(0, 255, 136, 0.4);
  transform: ${props => props.show ? 'translateX(0)' : 'translateX(400px)'};
  transition: transform 0.5s ease;
  z-index: 10000;
  pointer-events: auto;
  
  .achievement-title {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-bottom: 0.2rem;
  }
  
  .achievement-text {
    font-size: 1.1rem;
  }
`;

const HiddenElement = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  
  &:hover {
    background: rgba(0, 255, 136, 0.2);
    border-radius: 50%;
    animation: pulse 1s infinite;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
`;

interface EasterEgg {
  id: string;
  trigger: 'click' | 'sequence' | 'hover' | 'scroll';
  target?: string;
  sequence?: string[];
  position?: { x: number; y: number };
  message: string;
  achievement: string;
  discovered: boolean;
}

const EasterEggs: React.FC = () => {
  const [discoveredEggs, setDiscoveredEggs] = useState<Set<string>>(new Set());
  const [showMessage, setShowMessage] = useState<string | null>(null);
  const [showAchievement, setShowAchievement] = useState<string | null>(null);
  const [keySequence, setKeySequence] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const easterEggs: EasterEgg[] = [
    {
      id: 'konami',
      trigger: 'sequence',
      sequence: ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'],
      message: '🎮 Konami Code activated! You\'ve unlocked developer mode!',
      achievement: 'Code Master',
      discovered: false
    },
    {
      id: 'hidden1',
      trigger: 'click',
      position: { x: 50, y: 100 },
      message: '🕵️ You found the first hidden secret! Keep exploring...',
      achievement: 'Detective',
      discovered: false
    },
    {
      id: 'hidden2',
      trigger: 'click',
      position: { x: window.innerWidth - 100, y: 200 },
      message: '💎 Another secret discovered! You have keen eyes!',
      achievement: 'Eagle Eye',
      discovered: false
    },
    {
      id: 'scroll-master',
      trigger: 'scroll',
      message: '🌪️ Scroll Master! You\'ve explored every corner of the Zero Limits!',
      achievement: 'Explorer',
      discovered: false
    },
    {
      id: 'triple-click',
      trigger: 'click',
      target: '.app-title',
      message: '👑 You discovered the title secret! The Hall recognizes your dedication!',
      achievement: 'Hall Guardian',
      discovered: false
    }
  ];

  const showEasterEgg = (egg: EasterEgg) => {
    if (discoveredEggs.has(egg.id)) return;

    setDiscoveredEggs(prev => {
      const newSet = new Set(prev);
      newSet.add(egg.id);
      return newSet;
    });
    setShowMessage(egg.message);
    setShowAchievement(egg.achievement);

    // Hide achievement after 4 seconds
    setTimeout(() => {
      setShowAchievement(null);
    }, 4000);

    // Create celebration effect
    createCelebrationEffect();
  };

  const createCelebrationEffect = () => {
    const container = containerRef.current;
    if (!container) return;

    // Create floating particles
    for (let i = 0; i < 20; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 6px;
        height: 6px;
        background: #00ff88;
        border-radius: 50%;
        left: ${Math.random() * window.innerWidth}px;
        top: ${Math.random() * window.innerHeight}px;
        pointer-events: none;
        z-index: 10001;
      `;
      
      container.appendChild(particle);
      
      gsap.to(particle, {
        y: -200,
        x: Math.random() * 200 - 100,
        opacity: 0,
        duration: 2,
        ease: "power2.out",
        onComplete: () => {
          container.removeChild(particle);
        }
      });
    }
  };

  useEffect(() => {
    let scrollCount = 0;
    let titleClickCount = 0;
    let titleClickTimer: NodeJS.Timeout;

    // Konami code detection
    const handleKeyDown = (e: KeyboardEvent) => {
      const newSequence = keySequence.concat(e.code);
      setKeySequence(newSequence.slice(-10)); // Keep only last 10 keys
      
      const konamiEgg = easterEggs.find(egg => egg.id === 'konami');
      if (konamiEgg && konamiEgg.sequence) {
        const isKonamiCode = konamiEgg.sequence.every((key, index) => 
          newSequence[newSequence.length - konamiEgg.sequence!.length + index] === key
        );
        
        if (isKonamiCode && !discoveredEggs.has('konami')) {
          showEasterEgg(konamiEgg);
        }
      }
    };

    // Scroll detection
    const handleScroll = () => {
      scrollCount++;
      if (scrollCount > 50 && !discoveredEggs.has('scroll-master')) {
        const scrollEgg = easterEggs.find(egg => egg.id === 'scroll-master')!;
        showEasterEgg(scrollEgg);
      }
    };

    // Title triple-click detection
    const handleTitleClick = (e: Event) => {
      titleClickCount++;
      
      if (titleClickTimer) clearTimeout(titleClickTimer);
      
      titleClickTimer = setTimeout(() => {
        if (titleClickCount >= 3 && !discoveredEggs.has('triple-click')) {
          const titleEgg = easterEggs.find(egg => egg.id === 'triple-click')!;
          showEasterEgg(titleEgg);
        }
        titleClickCount = 0;
      }, 1000);
    };

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll);
    
    const titleElement = document.querySelector('.app-title');
    if (titleElement) {
      titleElement.addEventListener('click', handleTitleClick);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
      if (titleElement) {
        titleElement.removeEventListener('click', handleTitleClick);
      }
      if (titleClickTimer) clearTimeout(titleClickTimer);
    };
  }, [keySequence, discoveredEggs]);

  return (
    <EasterEggContainer ref={containerRef}>
      {/* Hidden clickable elements */}
      {easterEggs
        .filter(egg => egg.trigger === 'click' && egg.position)
        .map(egg => (
          <HiddenElement
            key={egg.id}
            style={{ 
              left: egg.position!.x, 
              top: egg.position!.y 
            }}
            onClick={() => showEasterEgg(egg)}
          />
        ))}

      {/* Secret message modal */}
      <SecretMessage visible={!!showMessage}>
        {showMessage}
        <button 
          className="close-btn"
          onClick={() => setShowMessage(null)}
        >
          ×
        </button>
      </SecretMessage>

      {/* Achievement notification */}
      <Achievement show={!!showAchievement}>
        <div className="achievement-title">Achievement Unlocked!</div>
        <div className="achievement-text">{showAchievement}</div>
      </Achievement>
    </EasterEggContainer>
  );
};

export default EasterEggs;